/**
 * TabBar — 멀티탭 네비게이션 바 (MDI 관용, vue-spring-fw 호환).
 *
 * 【역할】
 * - 사용자가 연 페이지들을 탭으로 관리 + 활성 탭 표시 + 우클릭 컨텍스트 메뉴(close all/others/right).
 * - 현재 경로가 tabs 에 없으면 자동 등록 (auto-register) — 메뉴 트리에서 라벨 조회.
 * - locale 변경 시 라벨 즉시 반영 (useI18nTickStore tick 구독 → re-render, Wave 19 W19-B).
 *
 * 【주요 기능】
 * - 탭 close 시 suppressAutoRegister ref 로 race condition 차단
 *   (flushSync 로 navigate 반영 → closeTab → 400ms 후 플래그 해제).
 * - onChange: 탭 클릭 → setActive + navigate.
 * - onEdit(remove): X 클릭 → closeTab + next 활성 탭으로 navigate.
 * - 컨텍스트 메뉴: 전체 닫기/다른 탭 닫기/오른쪽 탭 닫기 (다국어 TAB_CLOSE_* 라벨).
 *
 * 【보조 함수】
 * - findLabelByPath(tree, path) — 메뉴 트리에서 path 에 맞는 라벨 재귀 탐색.
 * - prettify(path) — 라벨 없을 때 경로를 "a > b > c" 형태로 변환.
 *
 * 【사용 위치】 RootLayout 내부 Header 아래 (TabBar 고정 표시).
 *
 * 【포팅 원본】 react-spring-fw TabBar.vue + useTabStore.ts.
 *
 * 【Wave 19 변경사항 (W19-B)】
 * - useI18nTickStore 구독으로 locale 변경 시 리렌더 보장.
 * - context menu 라벨을 t('TAB_CLOSE_*') 다국어 키로 전환.
 * - tabStore.refreshLabels 의 DASHBOARD skip 제거와 연동 (대시보드 탭도 갱신).
 *
 * 【의존성】 useTabStore/useMenuStore/useLabel/useI18nTickStore + antd Tabs/Dropdown.
 */
import { Tabs, Dropdown, theme as antdTheme } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useTabStore, DASHBOARD_KEY } from '@/store/tabStore'
import { useMenuStore, type MenuNode } from '@/store/menuStore'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'

function findLabelByPath(nodes: MenuNode[], path: string): string | null {
  for (const n of nodes) {
    if ((n.path ?? n.key) === path) return n.label
    if (n.children) {
      const r = findLabelByPath(n.children, path)
      if (r) return r
    }
  }
  return null
}

/** 메뉴 트리에서 path 에 해당하는 노드의 labelKey 를 재귀 탐색 (라벨 보존 로직 우회). */
function findLabelKeyByPath(nodes: MenuNode[], path: string): string | undefined {
  for (const n of nodes) {
    if ((n.path ?? n.key) === path) return n.labelKey
    if (n.children) {
      const r = findLabelKeyByPath(n.children, path)
      if (r) return r
    }
  }
  return undefined
}

// hard-coded fallbacks for well-known paths
const FALLBACK_LABELS: Record<string, string> = {
  '/dashboard': '대시보드'
}

function prettify(path: string): string {
  return path.replace(/^\//, '').replace(/\//g, ' > ') || path
}

/**
 * TabBar — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function TabBar() {
  const tabs = useTabStore((s) => s.openTabs)
  const activeKey = useTabStore((s) => s.activeKey)
  const openTab = useTabStore((s) => s.openTab)
  const closeTab = useTabStore((s) => s.closeTab)
  const closeAll = useTabStore((s) => s.closeAll)
  const closeOthers = useTabStore((s) => s.closeOthers)
  const closeRight = useTabStore((s) => s.closeRight)
  const setActive = useTabStore((s) => s.setActive)
  const initTabs = useTabStore((s) => s.initTabs)
  const tree = useMenuStore((s) => s.tree)
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = antdTheme.useToken()
  const { t } = useLabel()
  // W19-B: locale 전환 후 useI18nTickStore tick 이 증가하면 re-render
  //         → context menu 라벨 + 탭 라벨 즉시 반영
  useI18nTickStore((s) => s.tick)

  // close 계열 액션 직후 자동 등록 useEffect가 방금 닫은 탭을 되살리는 race 를 차단
  const suppressAutoRegister = useRef(false)

  // 최초 진입 시 대시보드가 항상 첫 자리에 보장되도록 초기화
  useEffect(() => {
    initTabs()
  }, [])

  // 현재 경로가 탭에 없으면 자동 등록
  useEffect(() => {
    const key = location.pathname
    if (key === '/' || key === '/login') return
    if (suppressAutoRegister.current) return
    if (!tabs.find((t) => t.key === key)) {
      const label = findLabelByPath(tree, key) ?? FALLBACK_LABELS[key] ?? prettify(key)
      const labelKey = findLabelKeyByPath(tree, key)
      openTab({ key, labelKey, label, path: key, closable: key !== DASHBOARD_KEY })
    } else if (activeKey !== key) {
      setActive(key)
    }
  }, [location.pathname, tree])

  if (tabs.length === 0) return null

  /**
   * antd Tabs 의 X 버튼 클릭 시 호출 (action='remove').
   * @param targetKey 닫을 탭 key (path)
   * @param action 'add' 는 hideAdd 옵션으로 차단되므로 사실상 'remove' 만
   */
  const onEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove'
  ) => {
    if (action !== 'remove') return
    const next = closeTab(String(targetKey))
    if (next) navigate(next)
  }

  /** 탭 클릭 → 활성 탭 갱신 + 라우팅 이동. 자동 등록 억제 중에는 무시. */
  const onChange = (key: string) => {
    if (suppressAutoRegister.current) return
    setActive(key)
    navigate(key)
  }

  // close 액션은 다음 순서로 처리한다.
  //  1) suppressAutoRegister 플래그로 뒤따르는 useEffect 의 자동 등록을 일시 차단
  //  2) flushSync 로 navigate 를 동기 반영
  //  3) store 에서 실제 탭 제거
  //  4) 다음 틱에 플래그 해제 (짧은 타임아웃으로 race 완전 차단)
  const releaseSuppress = () => {
    setTimeout(() => {
      suppressAutoRegister.current = false
    }, 400)
  }

  /** 컨텍스트 메뉴 '전체 닫기' — 모든 탭 닫고 대시보드로 이동. */
  const handleCloseAll = () => {
    suppressAutoRegister.current = true
    flushSync(() => navigate(DASHBOARD_KEY, { replace: true }))
    closeAll()
    releaseSuppress()
  }

  /** 컨텍스트 메뉴 '다른 탭 닫기' — 인자 key 를 활성 탭으로 만들고 나머지는 닫음. */
  const handleCloseOthers = (key: string) => {
    suppressAutoRegister.current = true
    if (location.pathname !== key) {
      flushSync(() => navigate(key, { replace: true }))
    }
    closeOthers(key)
    releaseSuppress()
  }

  /** 컨텍스트 메뉴 '오른쪽 탭 닫기' — idx 보다 큰 인덱스의 탭 일괄 제거. */
  const handleCloseRight = (idx: number, key: string) => {
    suppressAutoRegister.current = true
    const currIdx = tabs.findIndex((t) => t.key === location.pathname)
    if (currIdx > idx) {
      flushSync(() => navigate(key, { replace: true }))
    }
    closeRight(idx)
    releaseSuppress()
  }

  return (
    <Tabs
      type="editable-card"
      hideAdd
      animated={false}
      destroyOnHidden={false}
      activeKey={activeKey ?? undefined}
      onChange={onChange}
      onEdit={onEdit}
      items={tabs.map((tab, idx) => ({
        key: tab.key,
        closable: tab.closable ?? true,
        label: (
          <Dropdown
            trigger={['contextMenu']}
            menu={{
              items: [
                {
                  key: 'closeAll',
                  label: t('TAB_CLOSE_ALL', '전체 닫기'),
                  onClick: () => handleCloseAll()
                },
                {
                  key: 'closeOthers',
                  label: t('TAB_CLOSE_OTHERS', '다른 탭 닫기'),
                  onClick: () => handleCloseOthers(tab.key),
                  disabled: tabs.length <= 1
                },
                {
                  key: 'closeRight',
                  label: t('TAB_CLOSE_RIGHT', '오른쪽 탭 닫기'),
                  onClick: () => handleCloseRight(idx, tab.key),
                  disabled: idx === tabs.length - 1
                }
              ]
            }}
          >
            <span
              style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              onMouseDown={(e) => {
                // 우클릭(button===2) 시 antd Tabs의 탭 전환이 트리거되는 것을 차단
                if (e.button === 2) e.stopPropagation()
              }}
              onContextMenu={(e) => e.stopPropagation()}
            >
              {tab.labelKey ? t(tab.labelKey, tab.label) : tab.label}
            </span>
          </Dropdown>
        )
      }))}
      style={{
        paddingInline: token.padding,
        paddingTop: token.paddingXS,
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`
      }}
    />
  )
}
